import { useCallback, useMemo, useRef } from 'react';

import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/client';
import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';
import type { BroadcastableCommandConstructor } from '@atlassian/rovo-content-bridge-api/src/command/bridge-api-command-types';

import { getRovoBroadcastConfirmedActionsClientId } from '../clients/rovo-broadcast-confirmed-action-client';
import {
	RovoBroadcastConfirmedActionsCommand,
	type RovoBroadcastConfirmedActionsCommandProperties,
} from '../commands/rovo-broadcast-confirmed-action-command';

type Diposable = () => void;

/**
 * Custom React hook to manage subscription to Rovo confirmed actions via the content bridge API.
 *
 * This hook sets up a listener for the `RovoBroadcastConfirmedActionsCommand` and provides
 * a subscription mechanism for handling confirmed action events.
 *
 * @param id - The unique identifier used to create the client ID for the content bridge API.
 * @returns An object with a `subscribeToConfirmedActions` method to register a handler for confirmed actions.
 *
 * @example
 * const { subscribeToConfirmedActions } = useRovoConfirmedActions('some-id');
 * useEffect(() => {
 *   const unsubscribe = subscribeToConfirmedActions((props) => {
 *     // handle confirmed action
 *   });
 *   return () => unsubscribe();
 * }, [subscribeToConfirmedActions]);
 */
export const useRovoConfirmedActions = (id: string) => {
	const onCommandReceivedRef = useRef<
		((props: RovoBroadcastConfirmedActionsCommandProperties) => void) | null
	>(null);

	const onCommandReceived = useCallback(
		(properties: RovoBroadcastConfirmedActionsCommandProperties) => {
			onCommandReceivedRef.current?.(properties);
		},
		[],
	);

	const onMountCallback: RovoContentBridgeSetupHandler = useMemo(
		() =>
			(
				on: (
					arg0: BroadcastableCommandConstructor<RovoBroadcastConfirmedActionsCommandProperties>,
					arg1: (properties: RovoBroadcastConfirmedActionsCommandProperties) => void,
				) => void,
			) => {
				on(RovoBroadcastConfirmedActionsCommand, onCommandReceived);
			},
		[onCommandReceived],
	);

	const { ready, teardown } = useRovoContentBridgeAPI(
		getRovoBroadcastConfirmedActionsClientId(id),
		onMountCallback,
	);

	const subscribeToConfirmedActions = useCallback(
		(handler: (props: RovoBroadcastConfirmedActionsCommandProperties) => void): Diposable => {
			onCommandReceivedRef.current = handler;
			ready();
			return () => {
				if (onCommandReceivedRef.current === handler) {
					onCommandReceivedRef.current = null;
				}
				teardown();
			};
		},
		[ready, teardown],
	);

	return { subscribeToConfirmedActions };
};
